import * as Sentry from '@sentry/react';

export async function initSentryClient(): Promise<void> {
  try {
    // Fetch config from server endpoint
    const response = await fetch('/sentry-config.js');
    if (!response.ok) {
      console.log('[Sentry] Config endpoint not available');
      return;
    }

    // Execute the script to set window.SENTRY_CONFIG
    const scriptContent = await response.text();
    eval(scriptContent);

    const config = (window as any).SENTRY_CONFIG;

    if (!config) {
      console.log('[Sentry] No config found');
      return;
    }

    const { dsn, environment, gitCommit } = config;

    // Only initialize if production and DSN exists
    if (environment !== 'production' || !dsn) {
      console.log('[Sentry] Disabled in development');
      return;
    }

    Sentry.init({
      dsn,
      environment,
      release: gitCommit,
      enableLogs: true, // Enable structured logging
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
        Sentry.consoleLoggingIntegration({
          levels: ['log', 'info', 'warn', 'error', 'debug'],
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,

      // Only capture errors from our own scripts
      allowUrls: [
        /^https?:\/\/(www\.)?peipower\.ca/,
        /^https?:\/\/localhost/,
      ],

      // Ignore common browser extension and third-party garbage
      ignoreErrors: [
        // Browser extensions
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        /^safari-extension:\/\//,
        // Safari internal crap
        /webkit-masked-url/,
        /shell-plugins/,
        // Common benign errors
        /ResizeObserver loop/,
        /ResizeObserver loop completed with undelivered notifications/,
        // Network errors that aren't our problem
        /Network request failed/,
        /Failed to fetch/,
        /Load failed/,
        // User-initiated navigation
        /AbortError/,
        // Random browser garbage
        /Non-Error promise rejection captured/,
        /Object Not Found Matching Id/,
        /can't redefine non-configurable property/,
      ],

      // Block errors originating from these URL patterns
      denyUrls: [
        // Browser extensions
        /^chrome-extension:\/\//,
        /^moz-extension:\/\//,
        /^safari-extension:\/\//,
        /^safari-web-extension:\/\//,
        // Safari internal URLs
        /webkit-masked-url/,
        // Google Tag Manager / Analytics (if you use them)
        /googletagmanager\.com/,
        /google-analytics\.com/,
        // Facebook
        /connect\.facebook\.net/,
        // Third-party widgets
        /widget\./,
        /cdn\./,
      ],

      beforeSend(event, hint) {
        const error = hint.originalException;

        // Filter out errors with no stack trace (usually third-party garbage)
        if (event.exception?.values) {
          const hasFirstPartyFrame = event.exception.values.some((ex) =>
            ex.stacktrace?.frames?.some(
              (frame) =>
                frame.filename &&
                (frame.filename.includes('peipower.ca') ||
                  frame.filename.includes('localhost') ||
                  frame.filename.startsWith('/'))
            )
          );

          // If there's no frame from our code, drop it
          if (!hasFirstPartyFrame) {
            return null;
          }
        }

        return event;
      },
    });

    console.log('[Sentry] Initialized for production');
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

export { Sentry };
